import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import { alpha } from '@mui/material/styles'
import type { Tone } from '../status'
import { useDeclarativeText } from '../i18n/declarativeText'

/** Cor por tom: `bg` = fundo suave da pílula; `fg` = texto/versão escura legível.
 *  Fonte única compartilhada pela pílula (StatusChip) e pelo texto (StatusText). */
// eslint-disable-next-line react-refresh/only-export-components -- mapa de cores co-locado com os componentes de status
export const TONE_COLORS: Record<Tone, { bg: string; fg: string }> = {
  neutral: { bg: '#EDF1E1', fg: '#566043' },
  info: { bg: 'rgba(8,145,178,0.12)', fg: '#155e75' },
  warning: { bg: 'rgba(217,119,6,0.12)', fg: '#92400e' },
  success: { bg: 'rgba(22,163,74,0.12)', fg: '#166534' },
  error: { bg: 'rgba(220,38,38,0.12)', fg: '#991b1b' },
  primary: { bg: alpha('#00552C', 0.1), fg: '#00552C' },
  // Roxo (Samuel, 18/09/2026). Entrou com a situação Paralisado do contrato e com a
  // linha de Outra Transportadora na Frente: dois estados que não são erro, não são
  // espera e não são sucesso, e que nenhum dos cinco tons anteriores dizia sem
  // mentir sobre a gravidade.
  purple: { bg: 'rgba(124,58,237,0.12)', fg: '#6d28d9' },
  // Rosa (Samuel, 24/09/2026). Entrou com a situação Aguardando liberação: a
  // máquina já está no campo e o serviço não começou, o que não é o azul de quem
  // ainda vai, nem o âmbar de quem já está rodando.
  pink: { bg: 'rgba(219,39,119,0.12)', fg: '#be185d' },
}

/** Pílula de estado, na paleta sage/marca. Use em tabelas de fundo NEUTRO
 *  (cadastros, tickets, contratos) — onde a linha não é tingida por status. */
export function StatusChip({ label, tone = 'neutral' }: { label: string; tone?: Tone }) {
  const c = TONE_COLORS[tone]
  const tt = useDeclarativeText()
  return (
    <Chip
      label={tt(label)}
      size="small"
      sx={{
        bgcolor: c.bg,
        color: c.fg,
        fontWeight: 600,
        fontSize: '0.75rem',
        height: 22,
        borderRadius: '6px',
        '& .MuiChip-label': { px: 1 },
      }}
    />
  )
}

/** Estado como TEXTO colorido (negrito), SEM pílula. Padrão das grades cuja LINHA
 *  inteira já é tingida pela cor do status — evita "cor sobre cor" (pílula colorida
 *  dentro de uma linha colorida). Passe `color` para casar EXATO com a cor da linha
 *  (ex.: `tripStatusTint[code].fg`); senão usa a cor do `tone`. Mesmo idioma do
 *  Painel de Descarga e do Transporte de Máquina. */
export function StatusText({ label, tone = 'neutral', color }: { label: string; tone?: Tone; color?: string }) {
  const tt = useDeclarativeText()
  const localizedLabel = tt(label)
  return (
    <Box
      component="span"
      title={localizedLabel}
      sx={{
        fontWeight: 700,
        color: color ?? TONE_COLORS[tone].fg,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        minWidth: 0,
      }}
    >
      {localizedLabel}
    </Box>
  )
}
